import { useState, useEffect } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../lib/firebase/config";
import { useAuth } from "../lib/auth";

export type PlanId = "FREE" | "PRO" | "BUSINESS";
export type PlanStatus = "active" | "trialing" | "canceled" | "incomplete" | "past_due" | string;

export interface PlanData {
  planId: PlanId;
  status: PlanStatus;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  currentPeriodEnd?: number;
}

export function usePlan() {
  const { user } = useAuth();
  const [plan, setPlan] = useState<PlanData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user?.uid) {
      setPlan(null);
      setIsLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(
      doc(db, "users", user.uid),
      (snap) => {
        if (snap.exists()) {
          const d = snap.data();
          setPlan({
            planId: (d?.planId as PlanId) || "FREE",
            status: (d?.status as PlanStatus) || "active",
            stripeCustomerId: d?.stripeCustomerId,
            stripeSubscriptionId: d?.stripeSubscriptionId,
            currentPeriodEnd: d?.currentPeriodEnd ?? undefined,
          });
        } else {
          setPlan({ planId: "FREE", status: "active" });
        }
        setIsLoading(false);
      },
      (err) => {
        setPlan({ planId: "FREE", status: "active" });
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user?.uid]);

  const planId = plan?.planId ?? "FREE";
  const isPremium = planId === "PRO" || planId === "BUSINESS";

  return {
    planId,
    status: plan?.status ?? "active",
    stripeCustomerId: plan?.stripeCustomerId,
    stripeSubscriptionId: plan?.stripeSubscriptionId,
    currentPeriodEnd: plan?.currentPeriodEnd,
    isPremium,
    isLoading,
  };
}
